import React, { createContext, ReactNode, useEffect, useReducer } from 'react'
import { Dictionary } from 'lodash'

import {
  ChatHistoryLoadingState,
  Conversation,
  DatabaseHealth,
  DatabaseStatus,
  Feedback,
  FrontendSettings,
  frontendSettings,
  historyEnsure,
  historyList
} from '../api'

import { appStateReducer } from './AppReducer'
import { CustomizationPreferences } from '../components/Customization/CustomizationPanel'

export interface AppState {
  isChatHistoryOpen: boolean
  isHelpPanelOpen: boolean
  isCustomizationPanelOpen: boolean
  chatHistoryLoadingState: ChatHistoryLoadingState
  isDatabaseAvailable: DatabaseHealth
  chatHistory: Conversation[] | null
  filteredChatHistory: Conversation[] | null
  currentChat: Conversation | null
  frontendSettings: FrontendSettings | null
  feedbackState: { [answerId: string]: Feedback.Neutral | Feedback.Positive | Feedback.Negative }
  isLoading: boolean
  answerExecResult: { [answerId: string]: [] }
  authToken: string
  userLanguage: string
  username: string
  encryptedUsername: string
  initialQuestion: string
  isAuthenticated: boolean
  customizationPreferences: CustomizationPreferences
  isAutoAudioEnabled: boolean
  userData: Dictionary<string>
  filterKeys: Array<{id: string, label: string}>
}

export type Action =
  | { type: 'TOGGLE_CHAT_HISTORY' }
  | { type: 'TOGGLE_HELP_PANEL' }
  | { type: 'TOGGLE_CUSTOMIZATION_PANEL' }
  | { type: 'SET_DATABASE_STATUS'; payload: DatabaseHealth }
  | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE'; payload: ChatHistoryLoadingState }
  | { type: 'UPDATE_CURRENT_CHAT'; payload: Conversation | null }
  | { type: 'UPDATE_FILTERED_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'UPDATE_CHAT_HISTORY'; payload: Conversation }
  | { type: 'UPDATE_CHAT_TITLE'; payload: Conversation }
  | { type: 'DELETE_CHAT_ENTRY'; payload: string }
  | { type: 'DELETE_CHAT_HISTORY' }
  | { type: 'DELETE_CURRENT_CHAT_MESSAGES'; payload: string }
  | { type: 'FETCH_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'FETCH_FRONTEND_SETTINGS'; payload: FrontendSettings | null }
  | {
      type: 'SET_FEEDBACK_STATE'
      payload: { answerId: string; feedback: Feedback.Positive | Feedback.Negative | Feedback.Neutral }
    }
  | { type: 'GET_FEEDBACK_STATE'; payload: string }
  | { type: 'SET_ANSWER_EXEC_RESULT'; payload: { answerId: string; exec_result: [] } }
  | { type: 'SET_AUTH_TOKEN'; payload: string }
  | { type: 'SET_USER_LANGUAGE'; payload: string }
  | { type: 'SET_USERNAME'; payload: string }
  | { type: 'SET_ENCRYPTED_USERNAME'; payload: string }
  | { type: 'SET_INITIAL_QUESTION'; payload: string }
  | { type: 'SET_AUTHENTICATION_STATUS'; payload: boolean }
  | { type: 'UPDATE_CUSTOMIZATION_PREFERENCES'; payload: CustomizationPreferences }
  | { type: 'TOGGLE_AUTO_AUDIO'; payload: boolean }
  | { type: 'UPDATE_USER_DATA'; payload: Dictionary<string> }
  | { type: 'SET_FILTER_KEYS'; payload: Array<{id: string, label: string}> }

const initialState: AppState = {
  isChatHistoryOpen: false,
  isHelpPanelOpen: false,
  isCustomizationPanelOpen: false,
  chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
  chatHistory: null,
  filteredChatHistory: null,
  currentChat: null,
  isDatabaseAvailable: {
    database: false,
    status: DatabaseStatus.NotConfigured
  },
  frontendSettings: null,
  feedbackState: {},
  isLoading: true,
  answerExecResult: {},
  authToken: '',
  userLanguage: 'FR',
  username: '',
  encryptedUsername: '',
  initialQuestion: '',
  isAuthenticated: false,
  customizationPreferences: {
    responseSize: 'medium',
    documentsCount: 5,
    llmProvider: '' // Will be set from frontendSettings
  },
  isAutoAudioEnabled: false,
  userData: {},
  filterKeys: []
}

export const AppStateContext = createContext<
  | {
      state: AppState
      dispatch: React.Dispatch<Action>
    }
  | undefined
>(undefined)

type AppStateProviderProps = {
  children: ReactNode
}

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appStateReducer, initialState)

  // Load customization preferences, audio settings, and userData from localStorage on startup
  useEffect(() => {
    const loadPreferencesFromStorage = (): CustomizationPreferences | null => {
      try {
        const saved = localStorage.getItem('userCustomizationPreferences')
        if (saved) {
          const parsed = JSON.parse(saved)
          if (parsed && typeof parsed === 'object') {
            return {
              responseSize: parsed.responseSize || 'medium',
              documentsCount: typeof parsed.documentsCount === 'number' ? parsed.documentsCount : 5,
              llmProvider: parsed.llmProvider || ''
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load customization preferences from localStorage:', error)
      }
      return null
    }

    const loadAudioSettings = (): boolean | null => {
      try {
        const saved = localStorage.getItem('isAutoAudioEnabled')
        if (saved === null) {
          return null // Aucune préférence sauvegardée
        }
        return saved === 'true'
      } catch (error) {
        console.warn('Failed to load audio settings from localStorage:', error)
        return null
      }
    }

    const loadUserDataFromStorage = (): Dictionary<string> | null => {
      try {
        const saved = localStorage.getItem('userData')
        if (saved) {
          const parsed = JSON.parse(saved)
          if (parsed && typeof parsed === 'object') {
            return parsed
          }
        }
      } catch (error) {
        console.warn('Failed to load userData from localStorage:', error)
      }
      return null
    }

    const savedPreferences = loadPreferencesFromStorage()
    if (savedPreferences) {
      dispatch({ type: 'UPDATE_CUSTOMIZATION_PREFERENCES', payload: savedPreferences })
    }

    const savedAudioSetting = loadAudioSettings()
    // Ne dispatcher que si une préférence existe dans localStorage
    if (savedAudioSetting !== null) {
      dispatch({ type: 'TOGGLE_AUTO_AUDIO', payload: savedAudioSetting })
    }
    // Sinon, garder la valeur par défaut (false) définie dans initialState

    const savedUserData = loadUserDataFromStorage()
    if (savedUserData) {
      dispatch({ type: 'UPDATE_USER_DATA', payload: savedUserData })
    }
  }, [])

  useEffect(() => {
    // Check for cosmosdb config and fetch initial data here
    const fetchChatHistory = async (offset = 0): Promise<Conversation[] | null> => {
      const result = await historyList(offset, state.authToken, state.encryptedUsername)
        .then(response => {
          if (response) {
            dispatch({ type: 'FETCH_CHAT_HISTORY', payload: response })
          } else {
            dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
          }
          return response
        })
        .catch(_err => {
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null })
          console.error('There was an issue fetching your data.')
          return null
        })
      return result
    }

    const getHistoryEnsure = async (token: string) => {
      if (!state.authToken) return // Ne rien faire si le token n'est pas défini

      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading })
      historyEnsure(token)
        .then(response => {
          if (response?.database) {
            fetchChatHistory()
              .then(res => {
                if (res) {
                  dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success })
                  dispatch({ type: 'SET_DATABASE_STATUS', payload: response })
                } else {
                  dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
                  dispatch({
                    type: 'SET_DATABASE_STATUS',
                    payload: { database: false, status: DatabaseStatus.NotWorking }
                  })
                }
              })
              .catch(_err => {
                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
                dispatch({
                  type: 'SET_DATABASE_STATUS',
                  payload: { database: false, status: DatabaseStatus.NotWorking }
                })
              })
          } else {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
            dispatch({ type: 'SET_DATABASE_STATUS', payload: response })
          }
        })
        .catch(_err => {
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail })
          dispatch({ type: 'SET_DATABASE_STATUS', payload: { database: false, status: DatabaseStatus.NotConfigured } })
        })
    }

    getHistoryEnsure(state.authToken)
  }, [state.authToken])

  useEffect(() => {
    const getFrontendSettings = async () => {
      frontendSettings()
        .then(response => {
          const frontendData = response as FrontendSettings
          dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: frontendData })
        })
        .catch(_err => {
          console.error('There was an issue fetching your data.')
        })
    }
    getFrontendSettings()
  }, [])

  // Separate effect to handle LLM provider selection after both localStorage and frontendSettings are available  
  useEffect(() => {
    if (!state.frontendSettings) return // Wait for frontend settings to be loaded
    
    const currentLlmFromLocalStorage = state.customizationPreferences.llmProvider
    const availableProviders = state.frontendSettings?.available_llm_providers || []
    const defaultLlmProvider = state.frontendSettings?.default_llm_provider
    
    let finalLlmProvider = defaultLlmProvider
    
    // If there's a localStorage preference AND it's in available providers, use it
    if (currentLlmFromLocalStorage && availableProviders.includes(currentLlmFromLocalStorage)) {
      finalLlmProvider = currentLlmFromLocalStorage
      console.log(`[LLM SELECTION] Using localStorage preference: ${currentLlmFromLocalStorage}`)
      return // Don't need to update, already have the right one
    } else {
      console.log(`[LLM SELECTION] Using default LLM: ${defaultLlmProvider}`)
      if (currentLlmFromLocalStorage && !availableProviders.includes(currentLlmFromLocalStorage)) {
        console.warn(`[LLM SELECTION] localStorage LLM "${currentLlmFromLocalStorage}" not in available providers: ${availableProviders.join(', ')}`)
      }
    }
    
    // Update with the determined LLM provider (default)
    if (finalLlmProvider && finalLlmProvider !== currentLlmFromLocalStorage) {
      dispatch({ 
        type: 'UPDATE_CUSTOMIZATION_PREFERENCES', 
        payload: {
          ...state.customizationPreferences,
          llmProvider: finalLlmProvider
        }
      })
    }
  }, [state.frontendSettings]) // Only trigger when frontendSettings change

  // Load filter_keys from frontend settings if available
  useEffect(() => {
    if (state.frontendSettings?.filter_keys && state.frontendSettings.filter_keys.length > 0) {
      dispatch({
        type: 'SET_FILTER_KEYS',
        payload: state.frontendSettings.filter_keys
      })
    }
  }, [state.frontendSettings]) // Only trigger when frontendSettings change

  return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
}
